/** @jsxImportSource theme-ui */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import ReactSelect, { components } from 'react-select';
import { colors } from '../../tokens';
import { css, CSSObject } from 'theme-ui';
import rawCarrotTheme from '../../theme';
import { Stack, Text, Icon, IconProps, Container, FlexContainer, Divider } from '../..';
import { StylesConfig } from 'react-select';
import { NakedSelectProps, LegacyOption, CorrectOption } from './SelectTypes';

export const customReactSelectStyles = (props: NakedSelectProps): StylesConfig => ({
    option: (provided: CSSObject, state: any) => ({
        ...provided,
        ...css({
            variant: state.isFocused ? 'select.option-focused' : 'select.option-default',
        })(rawCarrotTheme),
    }),
    menu: (provided: CSSObject) => {
        return {
            ...provided,
            ...css({
                variant: props?.isSearchable === false ? 'select.menu-default' : 'select.menu-searchable',
            })(rawCarrotTheme),
        };
    },
    menuList: (base: any) => ({
        ...base,
        ...css({
            variant: 'select.menu-list-default',
        })(rawCarrotTheme),
    }),
    input: (provided: CSSObject) => ({
        ...provided,
        ...css({
            variant: 'select.input-default',
        })(rawCarrotTheme),
    }),
    clearIndicator: (provided: CSSObject) => ({
        ...provided,
        ...css({
            variant: 'select.clear-indicator-default',
        })(rawCarrotTheme),
    }),
    control: (provided: CSSObject, state: any) => {
        const variant = ['default'];

        if (state.selectProps.error) {
            variant[0] = 'error';
        }
        if (state.isFocused) {
            variant[0] = 'focused';
        }
        if (state.isDisabled) {
            variant[1] = 'disabled';
        }
        return {
            ...provided,
            ...css({
                variant: `select.control-${variant.join('-')}`,
            })(rawCarrotTheme),
        };
    },
    singleValue: (provided: CSSObject) => ({
        ...provided,
        ...css({
            variant: 'select.single-value-default',
        })(rawCarrotTheme),
    }),
    placeholder: (defaultStyles: CSSObject, state: any) => ({
        ...defaultStyles,
        ...css({
            variant: state.isDisabled ? 'select.placeholder-disabled' : 'select.placeholder-default',
        })(rawCarrotTheme),
    }),
    ...(props?.styleOverride ? props.styleOverride(props) : {}),
});

const DropdownIndicator = (props: any) => {
    const menuIsOpen = Boolean(props?.selectProps?.menuIsOpen);
    let iconColor = colors.base.carrotOrange as IconProps['color'];

    if (props.isDisabled) {
        iconColor = colors.base.black60;
    }
    if (props.selectProps.error) {
        iconColor = colors.base.errorRed;
    }
    if (props.isFocused) {
        iconColor = colors.base.carrotOrange;
    }
    return <Icon name={menuIsOpen ? 'up' : 'down'} color={iconColor} />;
};

const ValueContainer = ({ children }: React.PropsWithChildren<unknown>) => {
    return <FlexContainer maxWidth="100%">{children}</FlexContainer>;
};

const TruncatedText = ({ children }: React.PropsWithChildren<unknown>) => {
    return (
        <Text
            sx={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 'normal' }}
        >
            {children}
        </Text>
    );
};

const SingleValue = ({ children, ...props }: any) => (
    <components.SingleValue {...props}>
        <Stack direction="row" spacing="tiny">
            <TruncatedText>{props?.data?.name || children}</TruncatedText>
            <Text variant="subtext">{props?.data?.subLabel}</Text>
        </Stack>
    </components.SingleValue>
);

const Control = ({ children, ...rest }: any) => (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <components.Control {...rest}>
        <FlexContainer
            width="100%"
            justifyContent="space-between"
            paddingTopBottom="small"
            paddingLeftRight="medium"
            sx={{ lineHeight: '21px' }}
        >
            {children}
        </FlexContainer>
    </components.Control>
);

const NoOptionsMessage = (props: any) => {
    return (
        <components.NoOptionsMessage {...props}>
            <Text color="black40" fontStyle="italic">
                No results found
            </Text>
        </components.NoOptionsMessage>
    );
};
const MenuList = (props: any) => {
    const isSearchable = Boolean(props?.selectProps?.isSearchable);
    return (
        <Container paddingTopBottom={'4px'} shadow="sheet">
            <components.MenuList {...props}>
                {!isSearchable && (
                    <div sx={{ variant: 'select.option' }}>
                        <Stack spacing="16px">
                            <span>{props.selectProps.placeholder || 'Select option'}</span>
                            <Divider />
                        </Stack>
                    </div>
                )}
                {props.children}
            </components.MenuList>
        </Container>
    );
};

export function BaseSelect(props: NakedSelectProps): JSX.Element {
    const componentOverride = {
        ...(props.componentOverrides || {}),
        ...(props.optionComponent ? { Option: props.optionComponent } : {}),
    };
    const options = React.useMemo(
        () =>
            (props.options || []).map(({ value, id, ...other }: LegacyOption & CorrectOption) => ({
                ...other,
                value: value || id,
            })),
        [props.options]
    );
    const { isError, ...otherProps } = props;
    return (
        <ReactSelect
            {...{ ...otherProps, error: isError, options }}
            isDisabled={props.disabled}
            placeholder={props.placeholder || 'Select option'}
            styles={customReactSelectStyles(props)}
            components={
                {
                    MenuList,
                    Control,
                    SingleValue,
                    DropdownIndicator,
                    ValueContainer,
                    IndicatorSeparator: (): null => null,
                    NoOptionsMessage,
                    ...componentOverride,
                } as unknown
            }
        />
    );
}

export default function NakedSelect(props: NakedSelectProps): JSX.Element {
    return <BaseSelect {...props} />;
}
